import React, { useId, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';

import './ChatMapPanel.css';

/**
 * Neutral stand-in shown when no map has been supplied yet.
 *
 * Mirrors the "no API key configured" fallback in GeoBoundaryMap so an
 * unconfigured or not-yet-built map still looks intentional rather than broken.
 * The map ticket can drop this in favour of a real renderer, or keep it as the
 * loading / no-key state.
 */
function MapPlaceholder({ title }) {
    return (
        <div
            className="d-flex flex-column align-items-center justify-content-center flex-grow-1 w-100 h-100 bg-light"
            role="img"
            aria-label={`Map preview placeholder for ${title}`}
        >
            <span className="fs-2" aria-hidden="true">
                📍
            </span>
            <span className="text-muted small">Map preview</span>
        </div>
    );
}

MapPlaceholder.propTypes = {
    title: PropTypes.string.isRequired,
};

/**
 * Container for a map rendered inside a Bili chat response.
 *
 * Shows a small 16:9 thumbnail inline in the message bubble. Clicking it
 * expands the same content into a fullscreen modal, which minimizes back to the
 * thumbnail via the close button, a click outside the dialog, or the Escape key.
 *
 * The dialog wraps React Bootstrap's `Modal`, which supplies `role="dialog"`,
 * `aria-modal="true"`, the focus trap (`enforceFocus`), focus restoration
 * (`restoreFocus`), Escape handling (`keyboard`), backdrop click-away, body
 * scroll lock, and portal mounting. Hand-rolling those is how you end up with a
 * dialog that only closes when one specific button happens to hold focus.
 *
 * This component is deliberately agnostic about what renders inside it — pass a
 * map as `children` and it is used for both the thumbnail and the fullscreen
 * view. Two notes for whoever wires up a real map:
 *
 * 1. Thumbnail content should be **non-interactive** so the overlay button stays
 *    the only focusable element in the preview. For Google Maps that means
 *    `gestureHandling: 'none'`, `clickableIcons: false`, `keyboardShortcuts: false`.
 * 2. While the modal is open, `children` is mounted twice (thumbnail + dialog).
 *    That is free for the placeholder; a real map may want to skip initializing
 *    the hidden instance.
 */
export default function ChatMapPanel({ title, children, className }) {
    const panelId = useId();
    const titleId = `${panelId}-title`;
    const [expanded, setExpanded] = useState(false);
    const triggerRef = useRef(null);

    const content = children || <MapPlaceholder title={title} />;

    return (
        <div className={`chat-map-panel ${className || ''}`}>
            {/*
             * The preview is a SIBLING of the trigger, not a child of it. A
             * <button> may not contain interactive descendants, so nesting a
             * real map inside would be invalid HTML (axe: nested-interactive).
             * The transparent overlay also stops the map from swallowing the
             * click that is meant to expand it.
             */}
            <div className="position-relative">
                <div className="ratio ratio-16x9 overflow-hidden rounded border bg-light">
                    {content}
                </div>
                <button
                    type="button"
                    ref={triggerRef}
                    className="btn focus-ring position-absolute top-0 start-0 w-100 h-100 d-flex align-items-end justify-content-end p-2"
                    onClick={() => setExpanded(true)}
                    aria-haspopup="dialog"
                    aria-label={`Expand map: ${title}`}
                >
                    <span
                        className="badge text-bg-light border shadow-sm"
                        aria-hidden="true"
                    >
                        <i className="bi bi-arrows-fullscreen me-1" />
                        Expand
                    </span>
                </button>
            </div>
            <small className="d-block text-muted mt-1">{title}</small>

            <Modal
                show={expanded}
                onHide={() => setExpanded(false)}
                // Modal's own restoreFocus already does this; the explicit call
                // makes the behaviour obvious to a reader and deterministic in
                // tests once the exit transition has finished.
                onExited={() => triggerRef.current?.focus()}
                // Edge-to-edge on phones, where there is no room for a backdrop
                // anyway. From md up, ChatMapPanel.css sizes the dialog to 94%
                // of the viewport so a real click-away gutter exists -- plain
                // `fullscreen` covers every pixel and makes click-away
                // unreachable for an actual user.
                fullscreen="md-down"
                dialogClassName="chat-map-dialog"
                aria-labelledby={titleId}
            >
                <Modal.Header closeButton>
                    <Modal.Title id={titleId}>{title}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0 d-flex">{content}</Modal.Body>
            </Modal>
        </div>
    );
}

ChatMapPanel.propTypes = {
    /** Accessible name for both the thumbnail trigger and the dialog. */
    title: PropTypes.string.isRequired,
    /** Map to render in the thumbnail and the fullscreen view. */
    children: PropTypes.node,
    /** Optional CSS class name(s) applied to the wrapper element. */
    className: PropTypes.string,
};
